/**
 * Inline same-origin @font-face fonts as data URLs so the recorded
 * snapshot replays correctly on a different origin (the clintplay
 * session viewer at clintplay.linhares.sc). Without this, the
 * replayer's iframe tries to fetch the original font URLs cross-origin
 * and the browser CORS-blocks every one — the replay looks correct in
 * layout but uses fallback fonts and floods the console with errors.
 *
 * Approach:
 *  1. Walk every stylesheet we can read (same-origin only; cross-origin
 *     ones throw `SecurityError` on `cssRules` access — we skip them
 *     and the customer keeps font fallbacks for those, same as today).
 *  2. For each `@font-face` rule, parse its `src: url(...)` values.
 *  3. Fetch each font same-origin, base64-encode, build a new CSS text
 *     with `data:` URLs.
 *  4. Inject the rewritten rules as a fresh `<style>` element at the end
 *     of `<head>`. The browser's font-face table is keyed by
 *     (font-family, font-weight, font-style, font-stretch,
 *     unicode-range) — the LAST rule that matches a given descriptor
 *     tuple wins. We preserve every descriptor from the original rule
 *     except `src`, so each rewritten rule shadows exactly the original
 *     it's based on. Pages with multiple weights/styles per family get
 *     each variant inlined independently; pages with partial inlining
 *     (some weights skipped due to fetch failure) keep their other
 *     weights working via the original rules.
 *  5. Caller (SDK init) takes a fresh FullSnapshot once inlining
 *     resolves so the injected <style> is in the initial replay state
 *     rather than a mid-stream IncrementalSnapshot.
 *
 * Runs ONCE per session, on a microtask after SDK init, fire-and-forget.
 * Errors are swallowed (logged at warn) so a bad font URL never breaks
 * recording.
 */

const STYLE_TAG_ID = 'clintplay-inlined-fonts';
// Hard cap so a single page can't bloat the FullSnapshot beyond reason.
// Each font roundtrips uncompressed in the rrweb event (base64 = ~33%
// inflation on top of the binary). 2 MB of base64 ≈ 1.5 MB of font
// payload — comfortably under typical Next.js per-page font budgets
// (3-6 weights × 30-100 KB each) without making a misconfigured site
// torpedo its own replays.
const MAX_TOTAL_INLINE_BYTES = 2 * 1024 * 1024;

const fontFaceCache = new Map<string, string>(); // url → data URL

/**
 * url(...) values inside `src:` can be wrapped in quotes (single or
 * double) or unquoted. They can also be `local(...)` — those refer to
 * system-installed fonts and we leave them alone.
 */
function extractUrls(src: string): string[] {
  const urls: string[] = [];
  // Match url("..."), url('...'), or url(...)
  const re = /url\(\s*(?:"([^"]+)"|'([^']+)'|([^)]+?))\s*\)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src)) !== null) {
    urls.push(m[1] ?? m[2] ?? m[3]);
  }
  return urls;
}

function guessMimeFromUrl(url: string): string {
  // Strip query string and fragment first — Next.js and similar build
  // tools commonly cache-bust with `?v=hash`, which would otherwise
  // mask the real extension and demote the MIME to octet-stream.
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  if (path.endsWith('.woff2')) return 'font/woff2';
  if (path.endsWith('.woff')) return 'font/woff';
  if (path.endsWith('.ttf')) return 'font/ttf';
  if (path.endsWith('.otf')) return 'font/otf';
  if (path.endsWith('.eot')) return 'application/vnd.ms-fontobject';
  return 'application/octet-stream';
}

async function bufferToBase64(buf: ArrayBuffer): Promise<string> {
  // `btoa` chokes on bytes > 0x7f; build via Uint8Array → binary string in
  // 8 KB slices to stay under JS engines' argument-list size limit for
  // spread/apply (Safari historically caps low — ~10 K; V8 is higher
  // but inconsistent across versions). 8 KB is safe everywhere.
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

async function fetchAsDataUrl(url: string): Promise<string | null> {
  const cached = fontFaceCache.get(url);
  if (cached) return cached;
  try {
    const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    const mime = guessMimeFromUrl(url);
    const dataUrl = `data:${mime};base64,${await bufferToBase64(buf)}`;
    fontFaceCache.set(url, dataUrl);
    return dataUrl;
  } catch {
    return null;
  }
}

/**
 * Build a fresh @font-face rule by enumerating descriptors directly off
 * the parsed `style` object. Avoids the cssText-regex approach, which
 * mis-handles URLs that contain semicolons or parens (rare but
 * possible) and could emit malformed CSS the browser then refuses to
 * parse on the replay side.
 *
 * The browser exposes every set descriptor at `style[i]` (numeric
 * index → property name) — we read each one and re-emit it. The
 * `src` value is substituted with the rewritten one.
 */
function rebuildFontFaceRule(
  fontRule: CSSFontFaceRule,
  rewrittenSrc: string,
): string {
  const decls: string[] = [];
  const style = fontRule.style;
  for (let i = 0; i < style.length; i++) {
    const name = style[i];
    if (!name) continue;
    const value =
      name === 'src' ? rewrittenSrc : style.getPropertyValue(name);
    if (!value) continue;
    decls.push(`${name}: ${value}`);
  }
  // Some browsers don't enumerate `src` via the numeric index loop
  // (notably older WebKit) — fall back to an explicit append so the
  // rewritten src always lands in the output.
  if (!decls.some((d) => d.startsWith('src:'))) {
    decls.push(`src: ${rewrittenSrc}`);
  }
  return `@font-face { ${decls.join('; ')}; }`;
}

/**
 * Walk every stylesheet that we can read (same-origin), collect every
 * `@font-face` rule, fetch the fonts, and inject a new `<style>` block
 * with the rewritten rules.
 *
 * Returns the number of font rules inlined (mostly for tests and
 * diagnostics).
 */
export async function inlineFonts(): Promise<number> {
  if (typeof document === 'undefined' || typeof window === 'undefined') return 0;
  // Idempotency: prefer the data-attribute check because customer markup
  // might happen to have an element with our id (unlikely) and we don't
  // want to silently skip inlining in that case.
  if (document.querySelector(`style[data-clintplay-inlined-fonts="true"]`)) return 0;

  const rewrittenRules: string[] = [];
  let totalBytes = 0;

  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      // SecurityError on cross-origin stylesheets without
      // `crossorigin="anonymous"` + matching CORS headers. Same behaviour
      // as the CSSOM exposes everywhere — nothing we can do from JS.
      rules = sheet.cssRules;
    } catch {
      continue;
    }
    for (const rule of Array.from(rules)) {
      // CSSFontFaceRule type guards are spotty across TS DOM lib
      // versions; check by class name instead.
      if (rule.constructor.name !== 'CSSFontFaceRule') continue;
      const fontRule = rule as CSSFontFaceRule;
      const src = fontRule.style.getPropertyValue('src');
      if (!src) continue;

      const urls = extractUrls(src);
      let rewrittenSrc = src;
      let didReplace = false;
      for (const url of urls) {
        // Already a data URL — re-encoding it would just waste CPU and
        // count against the budget twice. Skip.
        if (url.startsWith('data:')) continue;
        // Resolve relative URLs against the document so the fetch hits
        // the right path. `local(...)` values aren't in `urls` (regex
        // skips them), so we won't try to fetch those.
        let absolute: string;
        try {
          absolute = new URL(url, document.baseURI).href;
        } catch {
          continue;
        }
        const dataUrl = await fetchAsDataUrl(absolute);
        if (!dataUrl) continue;

        // Rough size budget — base64 length × 0.75 is the binary
        // size. Bail before we exceed the cap.
        const estimatedBytes = dataUrl.length * 0.75;
        if (totalBytes + estimatedBytes > MAX_TOTAL_INLINE_BYTES) {
          // eslint-disable-next-line no-console
          console.warn('[Clintplay] font-inliner: budget reached, skipping remaining fonts');
          if (rewrittenRules.length > 0) {
            injectStyleTag(rewrittenRules.join('\n'));
          }
          return rewrittenRules.length;
        }
        totalBytes += estimatedBytes;

        // Replace the URL in the original `src` string. Escape `$` so
        // any sequence inside the data URL doesn't get interpreted by
        // `String.prototype.replace`'s pattern grammar.
        const escapedDataUrl = dataUrl.replace(/\$/g, '$$$$');
        // The original url(...) might be quoted; rebuild the call so we
        // don't accidentally break quoting.
        const escapedOriginal = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const originalCall = new RegExp(
          `url\\(\\s*(?:"${escapedOriginal}"|'${escapedOriginal}'|${escapedOriginal})\\s*\\)`,
        );
        rewrittenSrc = rewrittenSrc.replace(originalCall, `url("${escapedDataUrl}")`);
        didReplace = true;
      }

      // Only emit the rule if at least one URL got replaced — otherwise
      // we'd ship a duplicate rule with the same URLs, no point.
      if (didReplace) {
        rewrittenRules.push(rebuildFontFaceRule(fontRule, rewrittenSrc));
      }
    }
  }

  if (rewrittenRules.length === 0) return 0;
  injectStyleTag(rewrittenRules.join('\n'));
  return rewrittenRules.length;
}

function injectStyleTag(cssText: string): void {
  const styleEl = document.createElement('style');
  styleEl.id = STYLE_TAG_ID;
  // Marker for the recorder side — rrweb captures this attribute as
  // part of the snapshot, so the viewer can tell that this `<style>`
  // came from our inlining pass and is not part of the customer's HTML.
  styleEl.setAttribute('data-clintplay-inlined-fonts', 'true');
  styleEl.appendChild(document.createTextNode(cssText));
  (document.head ?? document.documentElement).appendChild(styleEl);
}
